import React, { useEffect } from "react";
import { Container, Row, Col } from "reactstrap";
import {
  FaPhone,
  FaMapMarkerAlt,
  FaEnvelope,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
} from "react-icons/fa";
import { Link } from "react-router-dom";

/*
  Token system
  ------------
  Color   bg-top #2B0B12 (deep maroon) → bg-bottom #150609 (near-black),
          hairline rgba(255,255,255,0.08), ink #F5EFE6, ink-muted #C9B8AE,
          accent (blood) #C41E3A, accent-soft #8C1D1D, gold #D9A441
  Type    Newsreader (headings/tagline) · Inter (body) · IBM Plex Mono (labels, bottom bar)
  Layout  Brand column with mission + contact "chips", link columns, social column,
          full-width ECG pulse line as the seam between footer and page above it.
  Signature  A single heartbeat line drawn across the top edge — the one visual motif,
             quiet everywhere else.
*/

const COLORS = {
  bgTop: "#2B0B12",
  bgBottom: "#150609",
  hairline: "rgba(255,255,255,0.08)",
  ink: "#F5EFE6",
  inkMuted: "#C9B8AE",
  accent: "#C41E3A",
  accentSoft: "#8C1D1D",
  gold: "#D9A441",
};

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/login", label: "Login" },
  { to: "/register", label: "Register" },
];

const SOCIALS = [
  { icon: FaFacebookF, href: "https://www.facebook.com/share/1NLTLxq6s9/?mibextid=wwXIfr", label: "Facebook" },
  { icon: FaInstagram, href: "https://www.instagram.com/aziz.welfaretrust?igsh=MTMweHQ5YjYyb3hncA%3D%3D&utm_source=qr", label: "Instagram" },
  
];

function injectFonts() {
  if (document.getElementById("awtf-fonts")) return;
  const link = document.createElement("link");
  link.id = "awtf-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Newsreader:ital,wght@0,500;0,600;1,500&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500;600&display=swap";
  document.head.appendChild(link);
}

const Footer = () => {
  useEffect(() => {
    injectFonts();
  }, []);

  return (
    <footer
      style={{
        position: "relative",
        background: `linear-gradient(180deg, ${COLORS.bgTop} 0%, ${COLORS.bgBottom} 100%)`,
        color: COLORS.ink,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <style>{`
        .awtf-heartbeat-path {
          stroke-dasharray: 340;
          stroke-dashoffset: 680;
          animation: awtfDraw 4.5s ease-in-out infinite;
        }
        @keyframes awtfDraw {
          0%   { stroke-dashoffset: 680; opacity: 0.35; }
          45%  { stroke-dashoffset: 0;   opacity: 1; }
          55%  { stroke-dashoffset: 0;   opacity: 1; }
          100% { stroke-dashoffset: -680; opacity: 0.35; }
        }
        .awtf-link {
          color: ${COLORS.inkMuted};
          text-decoration: none;
          position: relative;
          transition: color 0.2s ease;
        }
        .awtf-link:hover { color: ${COLORS.ink}; }
        .awtf-link::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: -3px;
          width: 0;
          height: 1px;
          background: ${COLORS.accent};
          transition: width 0.2s ease;
        }
        .awtf-link:hover::after { width: 100%; }
        .awtf-social {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: 1px solid ${COLORS.hairline};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: ${COLORS.ink};
          transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
        }
        .awtf-social:hover {
          background: ${COLORS.accent};
          border-color: ${COLORS.accent};
          transform: translateY(-2px);
        }
        .awtf-chip-icon {
          color: ${COLORS.gold};
          flex-shrink: 0;
          margin-top: 3px;
        }
        @media (prefers-reduced-motion: reduce) {
          .awtf-heartbeat-path { animation: none; stroke-dashoffset: 0; opacity: 0.8; }
        }
      `}</style>

      {/* Signature: heartbeat seam along the top edge */}
      <svg
        width="100%"
        height="28"
        viewBox="0 0 1200 28"
        preserveAspectRatio="none"
        style={{ display: "block" }}
      >
        <line x1="0" y1="14" x2="1200" y2="14" stroke={COLORS.hairline} strokeWidth="1" />
        <path
          className="awtf-heartbeat-path"
          d="M0,14 L470,14 L495,14 L508,3 L522,25 L536,14 L560,14 L575,8 L588,14 L1200,14"
          fill="none"
          stroke={COLORS.accent}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <Container>
        <Row className="py-3">
          {/* Brand and Contact */}
          <Col md="6" className="mb-4">
             <Link to="/" style={{ flexShrink: 0,marginBottom:20 }}>
                         <img src="/images/logo.png" height={54} alt="AWT Logo" />
                       </Link>

            <p
              style={{
                fontFamily: "'Newsreader', serif",
                fontStyle: "italic",
                fontSize: 18,
                color: COLORS.ink,
                maxWidth: 380,
                lineHeight: 1.5,
                marginBottom: 20,
              }}
            >
           AWT Blood Bank is a dedicated blood bank committed to providing safe and timely blood to those in need across Pakistan, serving humanity one donation at a time.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <FaPhone className="awtf-chip-icon" />
                <span style={{ color: COLORS.inkMuted, fontSize: 14 }}>+923238443294</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <FaMapMarkerAlt className="awtf-chip-icon" />
                <span style={{ color: COLORS.inkMuted, fontSize: 14 }}>7-A, Block 1, Sector C-2, Ghazi Chowk, Main College Road, Township, Lahore.</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <FaEnvelope className="awtf-chip-icon" />
                <span style={{ color: COLORS.inkMuted, fontSize: 14 }}>awtbloodbank@gmail.com</span>
              </div>
            </div>
          </Col>

          {/* Quick Links */}
          <Col md="3" className="mb-4">
            <h6
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: COLORS.gold,
                marginBottom: 18,
              }}
            >
              Quick Links
            </h6>

            <ul className="list-unstyled">
              {QUICK_LINKS.map((item) => (
                <li className="mb-3" key={item.to}>
                  <Link to={item.to} className="awtf-link">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </Col>

          {/* Social Links */}
          <Col md="3" className="mb-4">
            <h6
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: COLORS.gold,
                marginBottom: 18,
              }}
            >
              Follow Us
            </h6>

            <div className="d-flex gap-3">
              {SOCIALS.map(({ icon: Icon, href, label }) => (
                <a href={href} key={label} aria-label={label} className="awtf-social">
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </Col>
        </Row>
      </Container>

      {/* Bottom bar */}
      <div
        style={{
          borderTop: `1px solid ${COLORS.hairline}`,
          textAlign: "center",
          padding: "16px",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          letterSpacing: "0.02em",
          color: COLORS.inkMuted,
        }}
      >
        © {new Date().getFullYear()} AWT Blood Bank. All Rights Reserved.
      </div>
    </footer>
  );
};

export default Footer;
